import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Info, Check, ArrowLeft, HelpCircle } from 'lucide-react';
import { useHomeHref } from '@/hooks/useHomeHref';
import './auth.css';

// ─── Testimonials ────────────────────────────────────────────────────────────

const QUOTES = [
  {
    body: 'Welynk turned a 3-week placement scramble into a 2-day shortlist. The AI breakdown gave our team confidence to skip the spreadsheet altogether.',
    name: 'Priya Raman',
    role: 'Head of Placements · Veritas Institute',
  },
  {
    body: 'I went from sending 40 resumes a week to 4 — and got 3 interviews. The match scores actually mean something.',
    name: 'Aarav Mehta',
    role: 'Senior Frontend Engineer · Acme',
  },
  {
    body: "We've reduced time-to-hire by 38% across engineering. Every shortlist comes with an audit log, which our legal team loves.",
    name: 'Aman Sharma',
    role: 'Head of Talent · Stellaris Corp',
  },
];

const QuoteAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'rgba(111,216,192,0.22)',
        color: '#6FD8C0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

// ─── Left brand panel ─────────────────────────────────────────────────────────

export const AuthBrand: React.FC = () => {
  const [idx, setIdx] = useState(0);
  const homeHref = useHomeHref();

  useEffect(() => {
    const t = setInterval(() => setIdx((x) => (x + 1) % QUOTES.length), 6500);
    return () => clearInterval(t);
  }, []);

  const q = QUOTES[idx];

  return (
    <aside className="auth-aside">
      <div className="auth-grid" />

      <div className="auth-brand">
        <Link to={homeHref} className="wl-logo">
          <span className="we">we</span>
          <span className="lynk">lynk</span>
        </Link>
      </div>

      <div>
        <div className="auth-marketing">
          <h2>
            Connect talent.
            <br />
            Transform campus hiring.
          </h2>
          <p>
            One AI-powered platform connecting students, colleges, and companies — with ethical
            matching and full transparency.
          </p>

          <div className="auth-quote">
            <p className="q-body" key={idx}>
              &ldquo;{q.body}&rdquo;
            </p>
            <div className="q-by">
              <QuoteAvatar name={q.name} />
              <div>
                <div className="q-name">{q.name}</div>
                <div className="q-role">{q.role}</div>
              </div>
            </div>
            <div className="auth-quote-dots">
              {QUOTES.map((_, j) => (
                <i key={j} className={j === idx ? 'on' : ''} onClick={() => setIdx(j)} />
              ))}
            </div>
          </div>
        </div>

        <div className="auth-stats">
          <div className="auth-stat">
            <div className="v">96+</div>
            <div className="l">Institutes</div>
          </div>
          <div className="auth-stat-sep" />
          <div className="auth-stat">
            <div className="v">240k</div>
            <div className="l">Students</div>
          </div>
          <div className="auth-stat-sep" />
          <div className="auth-stat">
            <div className="v">38%</div>
            <div className="l">Faster hires</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

// ─── Auth shell wrapper ───────────────────────────────────────────────────────

export const AuthShell: React.FC<{ children: React.ReactNode; topAlign?: boolean }> = ({
  children,
  topAlign,
}) => {
  const homeHref = useHomeHref();
  return (
    <div className="auth-shell">
      <AuthBrand />
      <div className="auth-form-wrap">
        <div className="auth-form-scroll">
          <div className="auth-form" style={topAlign ? { marginTop: 0 } : undefined}>
            <div className="auth-mobile-logo">
              <Link to={homeHref} className="wl-logo">
                <span style={{ color: '#0F1A33' }}>we</span>
                <span style={{ color: '#6FD8C0' }}>lynk</span>
              </Link>
            </div>
            {children}
          </div>
        </div>
        <div className="auth-bottombar">
          <Link to={homeHref} className="auth-back-btn">
            <ArrowLeft size={14} />
            Back to home
          </Link>
          <a href="mailto:support@welynk.com" className="auth-help-btn">
            <HelpCircle size={14} />
            Need help?
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── Floating-label input ─────────────────────────────────────────────────────

interface FloatFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  type?: string;
  icon?: 'mail' | 'lock' | 'user';
  autoComplete?: string;
  error?: string;
  disabled?: boolean;
}

export const FloatField: React.FC<FloatFieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  icon,
  autoComplete,
  error,
  disabled,
}) => {
  const [showPw, setShowPw] = useState(false);
  const isPw = type === 'password';
  const effectiveType = isPw && showPw ? 'text' : type;
  const hasValue = value.length > 0;

  const IconCmp = icon === 'mail' ? Mail : icon === 'lock' ? Lock : icon === 'user' ? User : null;

  return (
    <div className={`float-field${hasValue ? ' has-value' : ''}${error ? ' has-error' : ''}`}>
      {/* Input must come before label for the CSS `+` adjacent-sibling selector to work */}
      <input
        id={id}
        className={`float-input${isPw ? ' has-pw' : ''}`}
        type={effectiveType}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder=" "
      />
      <label className="float-label" htmlFor={id}>
        {label}
      </label>
      {IconCmp && (
        <span className="field-ic">
          <IconCmp size={16} />
        </span>
      )}
      {isPw && (
        <button
          type="button"
          className="pw-toggle"
          onClick={() => setShowPw((s) => !s)}
          tabIndex={-1}
          aria-label={showPw ? 'Hide password' : 'Show password'}
        >
          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      )}
      {error && (
        <div className="field-err">
          <Info size={12} />
          {error}
        </div>
      )}
    </div>
  );
};

// ─── Password strength meter ──────────────────────────────────────────────────

function usePwStrength(pw: string) {
  const reqs = {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const score = Object.values(reqs).filter(Boolean).length as 0 | 1 | 2 | 3 | 4;
  return { score, reqs };
}

export const PwStrengthMeter: React.FC<{ pw: string }> = ({ pw }) => {
  const { score, reqs } = usePwStrength(pw);
  if (!pw) return null;

  const labels = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'] as const;
  const colors = [
    'var(--danger)',
    'var(--warn)',
    'var(--warn)',
    'var(--info)',
    'var(--teal-3)',
  ] as const;

  const reqList: [keyof typeof reqs, string][] = [
    ['length', '8+ chars'],
    ['upper', 'Uppercase'],
    ['number', 'Number'],
    ['symbol', 'Symbol'],
  ];

  return (
    <div className="pw-strength">
      <div className="bars">
        {([1, 2, 3, 4] as const).map((n) => (
          <i key={n} className={n <= score ? `on-${score}` : ''} />
        ))}
      </div>
      <div className="meta">
        <span className="l">Password strength</span>
        <span className="v" style={{ color: colors[score] }}>
          {labels[score]}
        </span>
      </div>
      <div className="reqs">
        {reqList.map(([k, l]) => (
          <span key={k} className={reqs[k] ? 'met' : ''}>
            <i>{reqs[k] && <Check size={8} strokeWidth={3.5} />}</i>
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── OTP digit input ──────────────────────────────────────────────────────────

interface OtpDigitInputProps {
  value: string[];
  onChange: (digits: string[]) => void;
  length?: number;
  error?: string | null;
  hint?: React.ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
}

/**
 * Six-digit (configurable) OTP entry grid: auto-advance on type, backspace/arrow
 * navigation, and paste-distributes across cells from the focused position.
 */
export const OtpDigitInput: React.FC<OtpDigitInputProps> = ({
  value,
  onChange,
  length = 6,
  error,
  hint,
  disabled,
  autoFocus = true,
  label = 'Verification code',
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) {
      // Delayed so it doesn't fight the page-transition/entry animation for
      // focus. Guard against firing after the user has already engaged with
      // the grid (typed fast, tabbed in) — stealing focus back to cell 0 at
      // that point drops whichever keystroke was in flight.
      const t = setTimeout(() => {
        const active = document.activeElement;
        const alreadyEngaged = inputsRef.current.some((el) => el === active);
        if (!alreadyEngaged) inputsRef.current[0]?.focus();
      }, 60);
      return () => clearTimeout(t);
    }
  }, [autoFocus]);

  const handleChange = (i: number, raw: string) => {
    const v = raw.replace(/\D/g, '');
    if (!v) {
      const next = [...value];
      next[i] = '';
      onChange(next);
      return;
    }
    if (v.length > 1) {
      // Paste: distribute across cells starting from position i
      const next = [...value];
      for (let j = 0; j < v.length && i + j < length; j++) next[i + j] = v[j];
      onChange(next);
      const lastFilled = Math.min(length - 1, i + v.length - 1);
      inputsRef.current[Math.min(length - 1, lastFilled + 1)]?.focus();
      return;
    }
    const next = [...value];
    next[i] = v[0];
    onChange(next);
    if (i < length - 1) inputsRef.current[i + 1]?.focus();
  };

  return (
    <div>
      <div
        className="otp-grid"
        role="group"
        aria-label={label}
        aria-describedby={error ? 'otp-error' : undefined}
      >
        {value.map((v, i) => (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            className={`otp-digit${v ? ' filled' : ''}`}
            value={v}
            inputMode="numeric"
            maxLength={i === 0 ? length : 1}
            disabled={disabled}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !value[i] && i > 0) inputsRef.current[i - 1]?.focus();
              if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < length - 1) inputsRef.current[i + 1]?.focus();
            }}
            onPaste={(e) => {
              e.preventDefault();
              handleChange(0, (e.clipboardData.getData('text') || '').trim());
            }}
            aria-label={`Digit ${i + 1} of ${length}`}
          />
        ))}
      </div>
      {error ? (
        <p
          id="otp-error"
          role="alert"
          style={{
            marginTop: 12,
            fontSize: 12.5,
            color: 'var(--danger)',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
          }}
        >
          <Info size={12} />
          {error}
        </p>
      ) : (
        hint && (
          <p style={{ marginTop: 12, fontSize: 12.5, color: 'var(--ink-4)', textAlign: 'center' }}>
            {hint}
          </p>
        )
      )}
    </div>
  );
};
